import { FC, memo } from 'react';
import { EdgeProps } from 'reactflow';

/**
 * __simplici_neon_flow__
 * The wire from flow-diagram-draggable-concept-3-neon.html, ported rather than
 * approximated. react-flow's BezierEdge picks its own control points, so the
 * curve never matched; these are the concept's own functions, unchanged in
 * behaviour — only translated from the DOM-measuring originals to react-flow's
 * already-resolved source/target coordinates.
 *
 * Three parts, exactly as the concept draws them:
 *   path   the wire        buildPath()
 *   head   an OPEN chevron arrowHeadPath() — two strokes meeting at a point,
 *          fill:none, not the filled polygon react-flow ships
 *   pulse  a 3.5r dot travelling the wire on animateMotion, staggered per edge
 *
 * Colour and glow come from css (.conditional-flow-steps), so a step's state
 * still tints its own wire.
 */

interface XY {
	x: number;
	y: number;
}

/** the concept's buildPath, verbatim in behaviour */
const buildPath = (a: XY, b: XY): string => {
	const span = Math.abs(b.x - a.x);

	// The concept's floors (40 and 30) assume its own spacing, where nodes sit
	// ~51px apart. In a table row the gap is a fraction of that, and a 40px
	// control offset on a 17px span puts c1 PAST the target and c2 BEHIND the
	// source — which draws the wire as a curl back on itself rather than a run.
	// Below that width the offset is taken from the span instead.
	const floor = (f: number) => (span < f * 2 ? span * 0.5 : f);

	if (Math.abs(b.y - a.y) < 20) {
		const dx = Math.max(span * 0.5, floor(40));
		return `M ${a.x} ${a.y} C ${a.x + dx} ${a.y}, ${b.x - dx} ${b.y}, ${b.x} ${b.y}`;
	}
	const dyDir = b.y > a.y ? 1 : -1;
	const vert = Math.abs(b.y - a.y) * 0.4;
	const c1x = a.x + (b.x - a.x) * 0.12;
	const c1y = a.y + dyDir * vert;
	const dx2 = Math.max(span * 0.45, floor(30));
	const c2x = b.x - dx2;
	const c2y = b.y;
	return `M ${a.x} ${a.y} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${b.x} ${b.y}`;
};

/** the concept's arrowHeadPath, verbatim: size 11, spread PI/6, unfilled */
const arrowHeadPath = (b: XY, prev: XY): string => {
	const angle = Math.atan2(b.y - prev.y, b.x - prev.x);
	const size = 11;
	const spread = Math.PI / 6;
	const p1x = b.x - size * Math.cos(angle - spread);
	const p1y = b.y - size * Math.sin(angle - spread);
	const p2x = b.x - size * Math.cos(angle + spread);
	const p2y = b.y - size * Math.sin(angle + spread);
	return `M ${p1x} ${p1y} L ${b.x} ${b.y} L ${p2x} ${p2y}`;
};

const prefersReducedMotion = () =>
	typeof window !== 'undefined' &&
	!!window.matchMedia &&
	window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const NeonEdge: FC<EdgeProps> = memo(
	({ id, sourceX, sourceY, targetX, targetY }) => {
		const a: XY = { x: sourceX, y: sourceY };
		const b: XY = { x: targetX, y: targetY };
		const d = buildPath(a, b);

		// the concept's own approach point for the head's angle
		// same reasoning as buildPath: the head's angle is taken from a point
		// BEHIND the target, and 30px behind a 17px span lands before the source
		const spanX = Math.abs(b.x - a.x);
		const dxHead = Math.max(
			spanX * (Math.abs(b.y - a.y) < 20 ? 0.5 : 0.35),
			Math.min(30, spanX * 0.5)
		);
		const head = arrowHeadPath(b, { x: b.x - dxHead, y: b.y });

		// stagger, as the concept does with `2.4 + i * 0.5` — the edge id is
		// stable and there is no index here, so it is derived from the id.
		const seed = Array.from(id).reduce((n, c) => n + c.charCodeAt(0), 0);
		const dur = 2.4 + (seed % 5) * 0.5;

		return (
			<>
				<path className="neon-wire" d={d} fill="none" />
				<path className="neon-head" d={head} fill="none" />
				{!prefersReducedMotion() && (
					<circle className="neon-pulse" r={3.5}>
						<animateMotion dur={`${dur}s`} repeatCount="indefinite" path={d} />
					</circle>
				)}
			</>
		);
	}
);

NeonEdge.displayName = 'NeonEdge';
